import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import Swal from 'sweetalert2';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { ArrowUpTrayIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../contexts/auth';
import { fetchEmployee } from '../services/api';
import { getSignatureBalance, createSignedDoc, isSentToProvider, normalizeMobile, toProviderSignPosition } from '../services/api_firme';
import { estraiMessaggiErrore } from '../services/utils';
import PopoverComponentCustom from '../components/popover_component';
import BackButton from '../components/back_button';
import Footer from '../components/footer';
import Fetching from '../components/fetching';
import FirmePdfSignaturePlacer from '../components/firme_pdf_signature_placer';
import FirmeSignerCard from '../components/firme_signer_card';
import ListBoxQueryMultiSelectEmployeesActiveContract from '../components/list_box_query_employee_active_contract';
import ModalBuyFirmeCredits from '../components/modal_buy_firme_credits';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const inputClass = 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:text-gray-500';
const labelClass = 'block mb-1 text-sm font-medium text-gray-700';
const getSignerErrors = (signer, position) => {
  const errors = [];
  if (!signer.cf?.trim()) errors.push('Codice fiscale obbligatorio');
  if (!signer.name?.trim()) errors.push('Nome obbligatorio');
  if (!signer.surname?.trim()) errors.push('Cognome obbligatorio');
  if (!signer.email?.trim()) errors.push('Email obbligatoria');
  else if (!EMAIL_RE.test(signer.email.trim())) errors.push('Email non valida');
  if (!signer.cellNumber?.trim()) errors.push('Cellulare obbligatorio per ricevere OTP');
  else if (!normalizeMobile(signer.cellNumber)) errors.push('Numero di cellulare non valido');
  if (!signer.descrizione?.trim()) errors.push('Descrizione della firma obbligatoria');
  if (!Array.isArray(position) || position.length === 0) errors.push('Posizionamento di almeno una firma obbligatorio');
  return errors;
};

export default function FirmeUpload() {
  const { codiceFiscale } = useParams();
  const { token, profile } = useContext(AuthContext);
  const [, setLocation] = useLocation();
  const [employee, setEmployee] = useState(null);
  const [employeeError, setEmployeeError] = useState(false);
  const [fetching, setFetching] = useState(!!codiceFiscale);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [signers, setSigners] = useState([]);
  // Ogni firmatario può avere più posizioni: { [signerKey]: [pos1, pos2, ...] }
  const [positions, setPositions] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [feaCredits, setFeaCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsError, setCreditsError] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const extraSignerIdRef = useRef(0);
  const submittedRef = useRef(false);

  const isProfileComplete = profile?.has_complete_profile;
  const showProfileWarning = profile?.customer_type === 'company' && !isProfileComplete && profile?.company?.email === profile?.the_real_user_email;
  const requiredCredits = file ? signers.length : 0;
  const insufficientCredits = feaCredits !== null && requiredCredits > feaCredits;

  const isCompanyUser =
    !!profile?.the_real_user_email &&
    !!profile?.company?.email &&
    profile.the_real_user_email.trim().toLowerCase() ===
      profile.company.email.trim().toLowerCase();

  const isConsultantUser =
    profile?.customer_type === "consultant" &&
    !!profile?.the_real_user_email &&
    !!profile?.company?.email &&
    profile.the_real_user_email.trim().toLowerCase() !==
      profile.company.email.trim().toLowerCase();

  const isEmployee = profile.customer_type === 'employee';

  /* RECUPERO DIPENDENTE DA URL */
  useEffect(() => {
    if (!codiceFiscale || !token) return;
    const controller = new AbortController();
    setFetching(true);
    setEmployeeError(false);
    fetchEmployee(token, codiceFiscale, 'documents')
      .then(res => {
        if (controller.signal.aborted) return;
        const normalized = {
          id: res?.employee?.id,
          cf: res?.cf,
          first_name: res?.first_name,
          last_name: res?.last_name,
          email: res?.email,
          phone_number: res?.phone_number || res?.employee?.phone_number || '',
        };
        if (normalized.id == null) {
          setEmployee(null);
          setEmployeeError(true);
        } else setEmployee(normalized);
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        setEmployee(null);
        setEmployeeError(true);
        Swal.fire({ icon: 'error', title: 'Errore', text: estraiMessaggiErrore(err) });
      })
      .finally(() => {
        if (!controller.signal.aborted) setFetching(false);
      });
    return () => controller.abort();
  }, [token, codiceFiscale]);

  /* RECUPERO CREDITI FEA */
  useEffect(() => {
    if (!token) {
      setFeaCredits(null);
      setCreditsLoading(false);
      setCreditsError(false);
      return;
    }
    let active = true;
    setCreditsLoading(true);
    setCreditsError(false);
    Promise.resolve().then(() => getSignatureBalance(token))
      .then(data => {
        if (!active) return;
        const value = profile?.customer_type === 'company' ? data?.free : data?.company_credits ?? data?.free;
        setFeaCredits(value != null && Number.isFinite(Number(value)) ? Number(value) : null);
      })
      .catch(err => {
        if (!active) return;
        console.error(err);
        setCreditsError(true);
        setFeaCredits(null);
      })
      .finally(() => {
        if (active) setCreditsLoading(false);
      });
    return () => { active = false; };
  }, [token, profile?.customer_type]);

  /* ACQUISTO FIRME */
  const handleBuyCredits = () => {
    setIsBuyCreditsModalOpen(true);
  };

  /* SELEZIONE DIPENDENTE - UN FIRMATARIO PER CARD */
  const normalizeEmployee = picked => ({
    id: picked?.employee?.id ?? picked?.id,
    cf: picked?.cf || picked?.employee?.cf || '',
    first_name: picked?.first_name || picked?.employee?.first_name || '',
    last_name: picked?.last_name || picked?.employee?.last_name || '',
    email: picked?.email || picked?.email_auth || picked?.employee?.email || '',
    phone_number: picked?.phone_number || picked?.employee?.phone_number || '',
  });

  const selectSignerEmployee = (key, picked) => {
    const selected = picked ? normalizeEmployee(picked) : null;

    // Impedisce di selezionare un dipendente già presente in un'altra scheda firmatario.
    if (selected) {
      const alreadySelected = signers.some(s =>
        s.key !== key &&
        (
          (selected.id != null &&
            s.employeeId != null &&
            String(s.employeeId) === String(selected.id)) ||
          (selected.cf?.trim() &&
            s.cf?.trim().toUpperCase() === selected.cf.trim().toUpperCase())
        )
      );
      if (alreadySelected) {
        Swal.fire({
          icon: 'warning',
          title: 'Firmatario già presente',
          text: 'Questo dipendente è già stato aggiunto come firmatario. Se devi inserire più firme per lo stesso dipendente, utilizza le posizioni di firma nella sua scheda.',
          confirmButtonColor: '#4f46e5',
        });
        return;
      }
    }

    if (key === 'employee') {
      setEmployee(selected);
    }

    setSigners(prev =>
      prev.map(s =>
        s.key === key
          ? {
              ...s,
              employeeId: selected?.id ?? null,
              cf: selected?.cf || '',
              name: selected?.first_name || '',
              surname: selected?.last_name || '',
              email: selected?.email || '',
              cellNumber: selected?.phone_number || '',
              locked: {
                cf: true,
                name: !!selected?.first_name,
                surname: !!selected?.last_name,
              },
            }
          : s
      )
    );

    setPositions(prev => ({
      ...prev,
      [key]: [],
    }));

    setActiveKey(key);
    setShowValidation(false);
  };

  useEffect(() => {
    setSigners(prev => {
      const first = prev.find(s => s.key === 'employee');
      if (!first) return [{
        key: 'employee', employeeId: employee?.id ?? null,
        cf: employee?.cf || '', name: employee?.first_name || '',
        surname: employee?.last_name || '', email: employee?.email || '',
        cellNumber: employee?.phone_number || '', descrizione: '',
        isEmployee: true, locked: { cf: true, name: !!employee?.first_name, surname: !!employee?.last_name },
      }, ...prev];
      return prev.map(s => s.key === 'employee' ? {
        ...s, employeeId: employee?.id ?? null,
        cf: employee?.cf || '', name: employee?.first_name || '',
        surname: employee?.last_name || '', email: employee?.email || '',
        cellNumber: employee?.phone_number || '',
        locked: { cf: true, name: !!employee?.first_name, surname: !!employee?.last_name },
      } : s);
    });
    setActiveKey(prev => prev || 'employee');
  }, [employee]);

  /* CARICAMENTO PDF */
  const handleSelectedFile = selected => {
    if (!selected || submitting) return;

    // Verifica formato PDF
    const isPdf = selected.name.toLowerCase().endsWith('.pdf') &&
      (!selected.type || selected.type === 'application/pdf');

    if (!isPdf) {
      Swal.fire({
        icon: 'error',
        title: 'File non valido',
        text: 'Seleziona un file PDF.',
        confirmButtonColor: '#4f46e5',
      });
      return;
    }

    // Verifica dimensione massima: 10 MB
    if (selected.size > MAX_FILE_SIZE) {
      Swal.fire({
        icon: 'warning',
        title: 'File troppo grande',
        text: 'Il documento supera la dimensione massima consentita di 10 MB. Seleziona un PDF di dimensioni inferiori.',
        confirmButtonColor: '#4f46e5',
      });
      return;
    }

    // File valido
    setFile(selected);
    setPositions({});

    if (!name.trim()) {
      setName(selected.name.replace(/\.pdf$/i, ''));
    }
  };

  const handleFileChange = e => {
    handleSelectedFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    if (submitting) return;
    dragCounter.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = submitting ? 'none' : 'copy';
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleFileDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (submitting) return;

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length !== 1) {
      Swal.fire({ icon: 'warning', title: 'Seleziona un solo documento', text: 'È possibile caricare un solo file PDF alla volta.' });
      return;
    }

    handleSelectedFile(files[0]);
  };

  /* GESTIONE FIRMATARI */
  const updateSigner = (key, field, value) => {
    if (field === 'cf') return;
    setSigners(prev => prev.map(s => s.key === key ? { ...s, [field]: value } : s));
  };
  const addSigner = () => {
    const key = `extra-${++extraSignerIdRef.current}`;

    setSigners(prev => [
      ...prev,
      {
        key,
        employeeId: null,
        cf: '',
        name: '',
        surname: '',
        email: '',
        cellNumber: '',
        descrizione: '',
        isEmployee: false,
        locked: { cf: true },
      },
    ]);

    setActiveKey(key);
    setShowValidation(false);
  };
  const removeSigner = key => {
    setSigners(prev => prev.filter(s => s.key !== key));
    setPositions(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setActiveKey(prev => prev === key ? 'employee' : prev);
  };

  /* GESTIONE POSIZIONI FIRMA */
  const handlePlace = (key, pos, index = null) => {
    setPositions(prev => {
      const current = prev[key] || [];

      if (index === null) {
        return {
          ...prev,
          [key]: [...current, pos],
        };
      }

      return {
        ...prev,
        [key]: current.map((item, i) =>
          i === index ? pos : item
        ),
      };
    });

    // Nasconde gli errori dopo il posizionamento
    setShowValidation(false);
  };

  const removePosition = (key, index) => {
    setPositions(prev => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== index),
    }));
  };

  /* VALIDAZIONE COMPLETA */
  const signerErrors = {};
  const missing = [];
  if (!employee) missing.push('Seleziona il dipendente principale');
  if (!file) missing.push('Seleziona un file PDF');
  if (!name.trim()) missing.push('Inserisci il nome del documento');
  if (!signers.length) missing.push('Aggiungi almeno un firmatario');
  signers.forEach((s, i) => {
    const errors = getSignerErrors(s, positions[s.key]);
    if (!s.employeeId) errors.unshift('Seleziona un dipendente');
    if (s.employeeId && signers.some(other => other.key !== s.key && other.employeeId === s.employeeId)) {
      errors.push('Dipendente già selezionato in un’altra card');
    }
    signerErrors[s.key] = errors;
    const who = s.isEmployee ? 'dipendente' : `firmatario ${i + 1}`;
    errors.forEach(error => missing.push(`${who}: ${error}`));
  });
  const totalPositions = Object.values(positions).reduce((total, list) => total + (Array.isArray(list) ? list.length : 0), 0);
  const canSubmit = missing.length === 0 && !submitting && !insufficientCredits;

  /* INVIO DOCUMENTO */
  const handleSubmit = async e => {
    e.preventDefault();
    if (submittedRef.current || submitting) return;
    if (file && file.size > MAX_FILE_SIZE) {
      Swal.fire({
        icon: 'warning',
        title: 'File troppo grande',
        text: 'La dimensione massima consentita per il documento PDF è di 10 MB.',
        confirmButtonColor: '#4f46e5',
      });
      return;
    }
    setShowValidation(true);
    if (missing.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Dati incompleti',
        text: 'Completa tutti i campi obbligatori dei firmatari, incluso il cellulare per ricevere l’OTP, e posiziona tutte le firme.',
      });
      return;
    }
    if (creditsLoading || creditsError || feaCredits === null || insufficientCredits) {
      await Swal.fire({
        icon: 'warning',
        title: insufficientCredits ? 'Crediti firma insufficienti' : 'Crediti non verificati',
        text: insufficientCredits
          ? `Firme disponibili: ${feaCredits}. Firme richieste: ${requiredCredits}.`
          : 'Impossibile verificare i crediti firma disponibili. Riprova tra poco.',
        confirmButtonColor: '#4f46e5',
      });
      return;
    }
    submittedRef.current = true;

    Swal.fire({
      title: 'Invio documento in corso',
      text: 'Il documento viene caricato e trasmesso al servizio di firma. Non chiudere la pagina.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    setSubmitting(true);
    let created;
    try {
      created = await createSignedDoc(token, {
        name: name.trim(),
        description: description.trim(),
        employeeIds: [...new Set(
          signers.map(s => s.employeeId).filter(id => id != null)
        )],
        file,
        firmatari: signers.map(s => ({
          cf: s.cf.trim().toUpperCase(),
          name: s.name.trim(),
          surname: s.surname.trim(),
          email: s.email.trim().toLowerCase(),
          cellNumber: normalizeMobile(s.cellNumber),
          descrizione: s.descrizione.trim(),
          fontSize: 'medium',
          // Il backend deve accettare signPositions (array) e convertirlo
          // nel formato richiesto da OpenAPI. Non inviare finché non è aggiornato.
          signPositions: (positions[s.key] || []).map(toProviderSignPosition),
        })),
        dueDate: dueDate || undefined,
      });
    } catch (error) {
      Swal.close();
      submittedRef.current = false;
      setSubmitting(false);
      await Swal.fire({
        icon: 'error',
        title: 'Invio non riuscito',
        text: estraiMessaggiErrore(error),
        confirmButtonColor: '#4f46e5',
      });
      return;
    }
    Swal.close();
    const warnings = [];
    if (!isSentToProvider(created)) warnings.push('Il documento è stato salvato ma non è stato possibile inviarlo al servizio di firma.');
    await Swal.fire(
      warnings.length
        ? { icon: 'warning', title: 'Documento creato con avvisi', text: warnings.join('\n') }
        : { icon: 'success', title: 'Documento inviato', text: 'I firmatari riceveranno una email con il link per firmare. Per firmare serviranno un documento di identità e il codice ricevuto via SMS.', confirmButtonColor: '#4f46e5', }
    );
    setLocation('/documenti?tab=firmati');
  };

  const handleAssignCreditsInfo = () => {
    Swal.fire({
      icon: "info",
      title: "Assegna crediti firma",
      html: `
        <p>
          Puoi assegnare crediti firma tramite la piattaforma
          <strong>Consultala</strong>, nella sezione
          <strong>Firma Documenti</strong>.
        </p>
        <p style="margin-top: 12px;">
          <strong>Attenzione:</strong> una volta assegnati i crediti
          all'azienda, non potranno essere riportati sul proprio credito
          Consultala, ma resteranno di proprietà dell'azienda HR.
        </p>
      `,
      confirmButtonText: "Ho capito",
      confirmButtonColor: "#4f46e5",
      allowOutsideClick: false,
    });
  };

  const handleRequestCreditsInfo = () => {
    Swal.fire({
      icon: "info",
      title: "Richiedi crediti firma",
      text: "Puoi chiedere al tuo consulente di aggiungere firme da caricare sul tuo profilo.",
      confirmButtonText: "Ho capito",
      confirmButtonColor: "#4f46e5",
      allowOutsideClick: false,
    });
  };

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex-grow">
        <PopoverComponentCustom title="Carica documento da firmare" />
        {showProfileWarning && (
          <div className="mb-6 bg-orange-100 p-4 text-center text-sm font-semibold text-orange-600">
            Il profilo aziendale non è completo. Compila i campi mancanti{' '}
            <Link to="/profilo/fatturazione" className="font-bold text-indigo-600 underline">cliccando qui</Link>.
          </div>
        )}
        <main className="mx-auto mt-6 max-w-[1600px] px-4 pb-10 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-center">
            <BackButton
              prevLocation={codiceFiscale
                ? `/dipendenti/${codiceFiscale}`
                : '/documenti?tab=firmati'
              }
            />
          </div>
          {fetching ? <Fetching /> : (
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {/* CONFIGURAZIONE FIRMA */}
              <div className="overflow-visible rounded-xl border border-gray-200 bg-white shadow-sm">
                {/* INTESTAZIONE */}
                <div className="rounded-t-xl border-b border-indigo-100 px-4 py-3 sm:px-5">
                  <div className="flex items-center gap-3">
                    {/* ICONA */}
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                        <DrawOutlinedIcon
                            sx={{ fontSize: 24 }}
                            className="text-indigo-700"
                        />
                    </div>

                    {/* TITOLO E SOTTOTITOLO */}
                    <div className="flex min-w-0 flex-col">
                      <h3 className="text-sm font-semibold uppercase text-indigo-800 sm:text-base">
                        Configurazione firma
                      </h3>

                      <p className="text-xs text-gray-500 pt-0.5">
                        Verifica le firme FEA disponibili prima di configurare i firmatari del documento
                      </p>
                    </div>
                  </div>
                </div>
            
                {/* FIRMA ELETTRONICA AVANZATA */}
                <div className="rounded-b-xl border border-indigo-100 bg-gradient-to-r from-indigo-100 via-indigo-50 to-white px-4 py-3 sm:px-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    {/* INFORMAZIONI FIRMA */}
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-indigo-800">
                            FEA — Firma Elettronica Avanzata
                          </span>
                          <span className="rounded-full bg-indigo-200 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                            FEA
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-600">
                          Firma elettronica con verifica dell'identità e codice OTP inviato al cellulare
                        </p>
                      </div>
                    </div>

                    {/* CREDITI DISPONIBILI E ACQUISTO */}
                    <div className="flex flex-wrap items-center gap-8 lg:justify-end">
                      <div className="border-l-2 border-indigo-200 pl-4">
                        <div className="text-xs font-bold uppercase text-indigo-700">
                          Firme FEA disponibili
                        </div>
                        <div className="mt-0 pt-0 text-indigo-900">
                          {creditsLoading ? (
                            <span className="text-sm font-medium text-indigo-500">
                              Caricamento...
                            </span>
                          ) : creditsError || feaCredits === null ? (
                            <span className="text-xs font-normal text-gray-500">
                              Non disponibili
                            </span>
                          ) : feaCredits}
                        </div>
                      </div>

                      {/* ACQUISTA FIRME - AZIENDA CHE PAGA IL PROPRIO ABBONAMENTO */}
                      {!isEmployee && isCompanyUser && profile?.can_purchase === true && (
                        <button
                          type="button"
                          onClick={handleBuyCredits}
                          disabled={submitting}
                          className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-md transition-colors hover:border-indigo-300 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <ShoppingCartOutlinedIcon fontSize="small" />
                          Acquista firme
                        </button>
                      )}

                      {/* ASSEGNA CREDITI - CONSULENTE */}
                      {!isEmployee && isConsultantUser && profile?.can_purchase === true && (
                        <button
                          type="button"
                          onClick={handleAssignCreditsInfo}
                          className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-md transition-colors hover:border-indigo-300 hover:bg-indigo-100"
                        >
                          <ShoppingCartOutlinedIcon fontSize="small" />
                          Assegna crediti
                        </button>
                      )}

                      {/* RICHIEDI CREDITI - AZIENDA CON ABBONAMENTO PAGATO DAL CONSULENTE */}
                      {!isEmployee && isCompanyUser && profile?.can_purchase === false && (
                        <button
                          type="button"
                          onClick={handleRequestCreditsInfo}
                          className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-md transition-colors hover:border-indigo-300 hover:bg-indigo-100"
                        >
                          <ShoppingCartOutlinedIcon fontSize="small" />
                          Richiedi crediti
                        </button>
                      )}

                    </div>
                  </div>
                </div>
              </div>

              {/* DOCUMENTO, FIRMATARI E ANTEPRIMA */}
              <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,0.98fr)_minmax(0,1.02fr)] xl:gap-6">
                <div className="min-w-0 space-y-5">
                  {/* DETTAGLI DOCUMENTO */}
                  <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                    {/* INTESTAZIONE */}
                    <div className="rounded-t-xl border-b border-indigo-100 bg-indigo-50/50 px-4 py-3 sm:px-6">
                      <div className="flex items-center gap-3">
                        {/* ICONA */}
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-200/50">
                          <UploadFileOutlinedIcon
                            sx={{ fontSize: 20 }}
                            className="text-indigo-700"
                          />
                        </div>
                        {/* TITOLO E SOTTOTITOLO */}
                        <div className="flex min-w-0 flex-col">
                          <h3 className="text-sm font-semibold uppercase text-indigo-800 sm:text-base">
                            Documento da firmare
                          </h3>
                          <p className="pt-0.5 text-xs text-gray-500">
                            Carica il documento PDF e inserisci le informazioni necessarie per la firma
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* CONTENUTO CARD */}
                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2 sm:p-5">
                      {/* FILE PDF */}
                      <div className="sm:col-span-2">
                        <label className={labelClass} htmlFor="firme-file">
                          File PDF <span className="text-red-500">*</span>
                        </label>

                        <div className="mt-2">
                          <label
                            htmlFor="firme-file"
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleFileDrop}
                            className={`group flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-4 text-center transition-colors ${
                              submitting
                                ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
                                : isDragging
                                  ? 'border-indigo-600 bg-indigo-100 ring-2 ring-indigo-200'
                                  : showValidation && !file
                                    ? 'border-red-300 bg-red-50 hover:bg-red-100'
                                    : file
                                      ? 'border-green-300 bg-green-50'
                                      : 'border-indigo-200 bg-indigo-50/40 hover:border-indigo-400 hover:bg-indigo-50'
                            }`}
                          >
                            {/* ICONA */}
                            <div className={`mb-3 flex h-12 w-12 items-center justify-center rounded-xl ${
                              file ? 'bg-green-100' : 'bg-indigo-100'
                            }`}>
                              {file && !isDragging ? (
                                <DocumentIcon className="h-6 w-6 text-green-700" />
                              ) : (
                                <ArrowUpTrayIcon className="h-6 w-6 text-indigo-700" />
                              )}
                            </div>

                            {/* TESTO */}
                            {file && !isDragging ? (
                              <>
                                <span className="max-w-full break-all text-sm font-semibold text-green-800">
                                  {file.name}
                                </span>

                                <span className="mt-1 text-xs text-green-700">
                                  Documento PDF selezionato
                                </span>

                                {!submitting && (
                                  <span className="mt-3 text-xs font-semibold text-indigo-600">
                                    Clicca per sostituire il documento
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="text-sm font-semibold text-indigo-700">
                                  {isDragging ? 'Rilascia il PDF qui' : 'Clicca o trascina un documento PDF'}
                                </span>

                                <span className="mt-1 text-xs text-gray-500">
                                  {isDragging ? 'Rilascia per caricare il documento' : 'Seleziona un PDF dal dispositivo oppure trascinalo in quest’area'}
                                </span>
                                <span className="mt-2 text-xs font-medium text-gray-500">
                                  Formato PDF — Dimensione massima: 10 MB
                                </span>
                              </>
                            )}

                            <input
                              id="firme-file"
                              type="file"
                              accept="application/pdf,.pdf"
                              onChange={handleFileChange}
                              disabled={submitting}
                              className="sr-only"
                              aria-invalid={showValidation && !file}
                              aria-describedby={showValidation && !file ? 'firme-file-error' : undefined}
                            />
                          </label>

                          {/* VALIDAZIONE */}
                          {showValidation && !file && (
                            <p
                              id="firme-file-error"
                              className="mt-2 text-xs font-medium text-red-600"
                            >
                              Seleziona un file PDF
                            </p>
                          )}
                        </div>
                      </div>

                      {/* NOME DOCUMENTO */}
                      <div>
                        <label className={labelClass} htmlFor="firme-name">
                          Nome documento <span className="text-red-500">*</span>
                        </label>

                        <input
                          id="firme-name"
                          type="text"
                          value={name}
                          onChange={e => setName(e.target.value)}
                          className={inputClass}
                          disabled={submitting}
                          required
                        />

                        {showValidation && !name.trim() && (
                          <p className="mt-1 text-xs text-red-600">
                            Inserisci il nome del documento.
                          </p>
                        )}
                      </div>

                      {/* DESCRIZIONE */}
                      <div className="sm:col-span-2">
                        <label className={labelClass} htmlFor="firme-description">
                          Descrizione
                        </label>
                        <textarea
                          id="firme-description"
                          rows={3}
                          value={description}
                          onChange={e => setDescription(e.target.value)}
                          className={inputClass}
                          disabled={submitting}
                        />
                      </div>

                      {/* SCADENZA */}
                      <div>
                        <label className={labelClass} htmlFor="firme-due">
                          Scadenza
                        </label>

                        <input
                          id="firme-due"
                          type="date"
                          value={dueDate}
                          onChange={e => setDueDate(e.target.value)}
                          className={inputClass}
                          disabled={submitting}
                        />
                      </div>
                    </div>
                  </div>
                  {file && (    
                    <div className="space-y-4">
                      {/* INTESTAZIONE FIRMATARI */}
                      <div className="flex items-center gap-3 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 sm:px-5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-200/50">
                          <PeopleOutlineOutlinedIcon sx={{ fontSize: 20 }} className="text-indigo-700" />
                        </div>
                        <div className="flex min-w-0 flex-col">
                          <h3 className="text-sm font-semibold uppercase text-indigo-800 sm:text-base">
                            Firmatari
                          </h3>
                          <p className="pt-0.5 text-xs text-gray-500">
                            Ogni firmatario dispone di una propria scheda. Seleziona il dipendente nella sua scheda e aggiungi le posizioni di firma sul PDF.
                          </p>
                        </div>
                      </div>

                      {/* UNA CARD INDIPENDENTE PER OGNI FIRMATARIO */}
                      {signers.map((s, i) => (
                        <div
                          key={s.key}
                          className={`overflow-visible rounded-xl border bg-white shadow-sm transition-shadow ${activeKey === s.key ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200'}`}
                        >
                          <div className="flex items-center justify-between gap-3 rounded-t-xl border-b border-indigo-100 bg-indigo-50/50 px-4 py-3 sm:px-6">
                            <div>
                              <h4 className="text-sm font-semibold uppercase text-indigo-800">
                                {s.isEmployee ? 'Firmatario 1' : `Firmatario ${i + 1}`}
                              </h4>
                              <p className="mt-1 text-xs text-gray-500">
                                Seleziona il dipendente e posiziona una o più firme sul documento
                              </p>
                            </div>
                            {!s.isEmployee && (
                              <button type="button" onClick={() => removeSigner(s.key)} disabled={submitting}
                                className="text-xs font-semibold text-red-600 hover:text-red-500 disabled:opacity-50">
                                Rimuovi firmatario
                              </button>
                            )}
                          </div>

                          <div className="space-y-3 p-4 sm:p-5">
                            {s.isEmployee && codiceFiscale && employeeError ? (
                              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                Impossibile caricare il dipendente. Torna alla pagina precedente e riprova.
                              </div>
                            ) : (
                              <>
                                <div className="space-y-2">
                                  <label className={labelClass}>Seleziona dipendente <span className="text-red-500">*</span></label>
                                  {s.employeeId ? (
                                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                                      <div className="text-xs text-green-800">
                                        <span className="font-semibold">Dipendente selezionato:</span>{' '}
                                        <span className="font-bold uppercase">{[s.name, s.surname].filter(Boolean).join(' ') || s.cf}</span>
                                      </div>
                                      {!(s.isEmployee && codiceFiscale) && (
                                        <button type="button" onClick={() => selectSignerEmployee(s.key, null)} disabled={submitting}
                                          className="rounded-md border border-green-300 bg-white px-3 py-1.5 text-xs font-semibold text-green-800 hover:bg-green-100 disabled:opacity-50">
                                          Cambia dipendente
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <ListBoxQueryMultiSelectEmployeesActiveContract
                                      token={token}
                                      selectedEmployees={[]}
                                      onChange={sel => {
                                        const picked = Array.isArray(sel) ? sel[0] : sel;
                                        if (picked?.id || picked?.employee?.id) selectSignerEmployee(s.key, picked);
                                      }}
                                      employeeRolePairs={[]}
                                      placeholder="Cerca e seleziona dipendente..."
                                      filterBy="all"
                                      pageSize={10}
                                      openDirection="top"
                                    />
                                  )}
                                </div>
                                <FirmeSignerCard
                                  signer={s}
                                  title="Dati del firmatario"
                                  positions={positions[s.key] || []}
                                  isActive={activeKey === s.key}
                                  onChange={(field, value) => updateSigner(s.key, field, value)}
                                  onActivate={() => setActiveKey(s.key)}
                                  onRemovePosition={index => removePosition(s.key, index)}
                                />
                              </>
                            )}
                            {showValidation && signerErrors[s.key]?.length > 0 && (
                              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                                <p className="mb-2 text-xs font-semibold text-red-700">Completa i dati obbligatori:</p>
                                <ul className="list-disc space-y-1 pl-4 text-xs text-red-600">
                                  {signerErrors[s.key].map(error => <li key={error}>{error}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* AGGIUNTA FIRMATARI */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={addSigner}
                          disabled={submitting}
                          className="rounded-md w-full border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                        >
                          Aggiungi firmatario
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {/* ANTEPRIMA E POSIZIONAMENTO FIRME */}
                <div className="min-w-0 xl:sticky xl:top-5">
                  <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                    {/* INTESTAZIONE ANTEPRIMA E POSIZIONAMENTO FIRME */}
                    <div className="rounded-t-xl border-b border-indigo-100 bg-indigo-50/50 px-4 py-3 sm:px-6">
                      <div className="flex items-center gap-3">
                        {/* ICONA */}
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-200/50">
                          <DrawOutlinedIcon
                            sx={{ fontSize: 20 }}
                            className="text-indigo-700"
                          />
                        </div>

                        {/* TITOLO E SOTTOTITOLO */}
                        <div className="flex min-w-0 flex-col">
                          <h3 className="text-sm font-semibold uppercase text-indigo-800 sm:text-base">
                            Anteprima e posizionamento firme
                          </h3>
                          <p className="pt-0.5 text-xs text-gray-500">
                             Seleziona un firmatario e clicca sul PDF per aggiungere tutte le posizioni di firma necessarie
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* CONTENUTO CARD */}
                    <div className="min-w-0 p-3 sm:p-4">
                      {file && (
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-xs">
                          <span className="font-medium text-indigo-900">
                            Firmatario attivo: {signers.find(s => s.key === activeKey)?.name || 'Nessuno'} {signers.find(s => s.key === activeKey)?.surname || ''}
                          </span>
                          <span className="text-indigo-700">{totalPositions} posizioni totali</span>
                        </div>
                      )}
                      <FirmePdfSignaturePlacer
                        file={file}
                        signers={signers.map(s => ({
                          key: s.key,
                          label: `${s.name} ${s.surname}`.trim() || 'Firmatario',
                        }))}
                        activeKey={activeKey}
                        positions={positions}
                        onPlace={handlePlace}
                        onRemovePosition={removePosition}
                        onActivate={setActiveKey}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* CONFERMA E INVIO */}
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                {/* INTESTAZIONE CONFERMA E INVIO */}
                <div className="rounded-t-xl border-b border-indigo-100 bg-indigo-50/50 px-4 py-3 sm:px-6">
                  <div className="flex items-center gap-3">
                    {/* ICONA */}
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-200/50">
                      <TaskAltOutlinedIcon
                        sx={{ fontSize: 20 }}
                        className="text-indigo-700"
                      />
                    </div>
                    {/* TITOLO E SOTTOTITOLO */}
                    <div className="flex min-w-0 flex-col">
                      <h3 className="text-sm font-semibold uppercase text-indigo-800 sm:text-base">
                        Conferma e invio
                      </h3>
                      <p className="pt-0.5 text-xs text-gray-500">
                        Controlla i dati del documento e le posizioni delle firme prima dell'invio
                      </p>
                    </div>
                  </div>
                </div>

                {/* CONTENUTO CARD */}
                <div className="space-y-5 p-4 sm:p-6">
                  {showValidation && missing.length > 0 && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                      <p className="mb-2 text-sm font-semibold text-red-700">
                        Completa le seguenti informazioni:
                      </p>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-red-600">
                        {missing.map((m, i) => (
                          <li key={`${i}-${m}`}>{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {insufficientCredits && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                      Firme FEA insufficienti. Disponibili: <strong>{feaCredits}</strong>.
                      Richieste: <strong>{requiredCredits}</strong>.
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-4">
                    {/* RIEPILOGO */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-600">
                      <span>
                        <span className="font-semibold">Tipo firma:</span> FEA
                      </span>
                      <span className="hidden text-gray-300 sm:inline">|</span>
                      <span>
                        <span className="font-semibold">Firmatari:</span> {file ? signers.length : 0}
                      </span>
                      <span className="hidden text-gray-300 sm:inline">|</span>
                      <span>
                        <span className="font-semibold">Crediti richiesti:</span> {requiredCredits}
                      </span>
                      <span className="hidden text-gray-300 sm:inline">|</span>
                      <span>
                        <span className="font-semibold">Posizioni firma:</span> {totalPositions}
                      </span>
                    </div>

                    {/* PULSANTE INVIO E MESSAGGIO CREDITI */}
                    <div className="flex w-full flex-col items-end gap-2 sm:w-auto">
                      <button
                        type="submit"
                        disabled={submitting || insufficientCredits || creditsLoading || creditsError || feaCredits === null}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                      >
                        <DrawOutlinedIcon fontSize="small" />
                        {submitting ? 'Invio in corso...' : 'Invia documento per la firma'}
                      </button>

                      {insufficientCredits && (
                        <p className="text-xs font-medium text-red-600">
                          Crediti firma insufficienti per l'invio del documento.
                          Disponibili: {feaCredits} — Richiesti: {requiredCredits}.
                        </p>
                      )}

                      {!insufficientCredits && (creditsLoading || creditsError || feaCredits === null) && (
                        <p className="text-xs text-amber-700">
                          {creditsLoading
                            ? 'Verifica dei crediti firma in corso...'
                            : 'Impossibile verificare i crediti firma disponibili.'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </main>
      </div>
      {/* MODALE ACQUISTO FIRME FEA */}
      <ModalBuyFirmeCredits
        open={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
        availableCredits={feaCredits}
        useFreeCredits={profile?.customer_type === 'company'}
        token={token}
        storageScope={JSON.stringify([profile?.the_real_user_email, profile?.company?.id, profile?.company?.email])}
        enabled={profile?.customer_type !== 'employee'}
        onBalance={value => { setFeaCredits(value); setCreditsError(false); }}
      />
      <Footer />
    </div>
  );
}






// import React, { useContext, useEffect, useState } from 'react';
// import { useLocation, useParams } from 'wouter';
// import Swal from 'sweetalert2';
// import { AuthContext } from '../contexts/auth';
// import { fetchEmployee } from '../services/api';
// import { createSignedDoc, isSentToProvider, normalizeMobile, toProviderSignPosition } from '../services/api_firme';
// import { estraiMessaggiErrore } from '../services/utils';
// import PopoverComponentCustom from '../components/popover_component';
// import BackButton from '../components/back_button';
// import Footer from '../components/footer';
// import Fetching from '../components/fetching';
// import FirmePdfSignaturePlacer from '../components/firme_pdf_signature_placer';
// import FirmeSignerCard from '../components/firme_signer_card';
// import ListBoxQueryMultiSelectEmployeesActiveContract from '../components/list_box_query_employee_active_contract';

// const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// const inputClass =
//   'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:text-gray-500';
// const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

// export default function FirmeUpload() {
//   const { codiceFiscale } = useParams();
//   const { token, profile } = useContext(AuthContext);
//   const [, setLocation] = useLocation();

//   const [file, setFile] = useState(null);
//   const [employee, setEmployee] = useState(null);
//   const [employeeError, setEmployeeError] = useState(false);
//   const [fetching, setFetching] = useState(!!codiceFiscale);
//   const [name, setName] = useState('');
//   const [description, setDescription] = useState('');
//   const [dueDate, setDueDate] = useState(''); // 'YYYY-MM-DD'
//   const [signers, setSigners] = useState([]); // { key, cf, name, surname, email, cellNumber, descrizione, isEmployee }
//   const [positions, setPositions] = useState({}); // { [key]: { pag, x, y, pageH } }
//   const [activeKey, setActiveKey] = useState(null);
//   const [submitting, setSubmitting] = useState(false);

//   useEffect(() => {
//     if (!codiceFiscale || !token) return;
//     setFetching(true);
//     setEmployeeError(false);
//     fetchEmployee(token, codiceFiscale, 'documents')
//       .then((res) => {
//         const normalised = {
//           id: res?.employee?.id,
//           cf: res?.cf,
//           first_name: res?.first_name,
//           last_name: res?.last_name,
//           email: res?.email,
//           phone_number: res?.employee?.phone_number,
//         };
//         if (normalised.id == null) {
//           setEmployee(null);
//           setEmployeeError(true);
//         } else {
//           setEmployee(normalised);
//         }
//       })
//       .catch((error) => {
//         setEmployee(null);
//         setEmployeeError(true);
//         Swal.fire({ icon: 'error', title: 'Errore', text: estraiMessaggiErrore(error) });
//       })
//       .finally(() => setFetching(false));
//   }, [token, codiceFiscale]);

//   useEffect(() => {
//     if (!employee) {
//       setSigners((prev) => prev.filter((s) => !s.isEmployee));
//       setPositions((prev) => {
//         const { employee: _removed, ...rest } = prev;
//         return rest;
//       });
//       setActiveKey((prev) => (prev === 'employee' ? null : prev));
//       return;
//     }
//     const employeeSigner = {
//       key: 'employee',
//       cf: employee.cf || '',
//       name: employee.first_name || '',
//       surname: employee.last_name || '',
//       email: employee.email || employee.email_auth || '',
//       cellNumber: employee.phone_number || employee.employee?.phone_number || '',
//       descrizione: 'Firma del dipendente',
//       isEmployee: true,
//       // read-only solo se il record fornisce il valore, altrimenti va digitato
//       locked: { cf: !!employee.cf, name: !!employee.first_name, surname: !!employee.last_name },
//     };
//     setSigners((prev) => [employeeSigner, ...prev.filter((s) => !s.isEmployee)]);
//     setPositions((prev) => {
//       const { employee: _removed, ...rest } = prev;
//       return rest;
//     });
//     setActiveKey('employee');
//   }, [employee]);

//   const handleFileChange = (e) => {
//     const selected = e.target.files?.[0];
//     if (!selected) return;
//     const isPdf =
//       selected.type === 'application/pdf' ||
//       (selected.type === '' && selected.name.toLowerCase().endsWith('.pdf'));
//     if (!isPdf) {
//       e.target.value = '';
//       Swal.fire({ icon: 'error', title: 'File non valido', text: 'Seleziona un file PDF.' });
//       return;
//     }
//     setFile(selected);
//     setPositions({});
//     if (!name.trim()) setName(selected.name.replace(/\.pdf$/i, ''));
//   };

//   const updateSigner = (key, field, value) =>
//     setSigners((prev) => prev.map((s) => (s.key === key ? { ...s, [field]: value } : s)));

//   const addSigner = () => {
//     const key = `extra-${Date.now()}`;
//     setSigners((prev) => [
//       ...prev,
//       { key, cf: '', name: '', surname: '', email: '', cellNumber: '', descrizione: '', isEmployee: false },
//     ]);
//     setActiveKey(key);
//   };

//   // Legale rappresentante dal profilo aziendale: il profilo non ha il suo codice fiscale, che va digitato
//   const legal = profile?.customer_type === 'company' ? profile?.profile : null;
//   const canAddLegal = !!(legal?.legal_name || legal?.legal_surname) && !signers.some((s) => s.key === 'legal');

//   const addLegalSigner = () => {
//     setSigners((prev) => [
//       ...prev,
//       {
//         key: 'legal',
//         cf: '',
//         name: legal?.legal_name || '',
//         surname: legal?.legal_surname || '',
//         email: legal?.email_auth || '',
//         cellNumber: legal?.phone_number || '',
//         descrizione: 'Firma del legale rappresentante',
//         isEmployee: false,
//       },
//     ]);
//     setActiveKey('legal');
//   };

//   const removeSigner = (key) => {
//     setSigners((prev) => prev.filter((s) => s.key !== key));
//     setPositions((prev) => {
//       const { [key]: _removed, ...rest } = prev;
//       return rest;
//     });
//     setActiveKey((prev) => (prev === key ? null : prev));
//   };

//   const missing = [];
//   if (!file) missing.push('Seleziona un file PDF');
//   if (!employee) missing.push('Seleziona un dipendente');
//   if (!name.trim()) missing.push('Inserisci il nome del documento');
//   signers.forEach((s, i) => {
//     const who = s.isEmployee ? 'dipendente' : `firmatario ${i + 1}`;
//     if (!s.cf.trim() || !s.name.trim() || !s.surname.trim()) missing.push(`Completa i dati del ${who}`);
//     if (!EMAIL_RE.test(s.email.trim())) missing.push(`Inserisci un'email valida per il ${who}`);
//     if (!normalizeMobile(s.cellNumber)) missing.push(`Inserisci un cellulare valido per il ${who}`);
//     if (!positions[s.key]) missing.push(`Posiziona la firma del ${who}`);
//   });
//   const canSubmit = missing.length === 0 && !submitting;

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!canSubmit) return;
//     setSubmitting(true);

//     let created;
//     try {
//       created = await createSignedDoc(token, {
//         name: name.trim(),
//         description: description.trim(),
//         employeeId: employee.id,
//         file,
//         firmatari: signers.map((s) => ({
//           cf: s.cf.trim().toUpperCase(),
//           name: s.name.trim(),
//           surname: s.surname.trim(),
//           email: s.email.trim().toLowerCase(),
//           cellNumber: normalizeMobile(s.cellNumber),
//           descrizione: s.descrizione?.trim() || '',
//           fontSize: 'medium',
//           signPosition: toProviderSignPosition(positions[s.key]),
//         })),
//         dueDate: dueDate || undefined,
//       });
//     } catch (error) {
//       setSubmitting(false);
//       Swal.fire({ icon: 'error', title: 'Invio non riuscito', text: estraiMessaggiErrore(error) });
//       return;
//     }

//     // Da qui il documento esiste: non si torna mai al form, altrimenti un nuovo invio lo duplicherebbe.
//     const warnings = [];
//     if (!isSentToProvider(created)) {
//       warnings.push('Il documento è stato salvato ma non è stato possibile inviarlo al servizio di firma.');
//     }

//     await Swal.fire(
//       warnings.length
//         ? { icon: 'warning', title: 'Documento creato con avvisi', html: warnings.join('<br/>') }
//         : { icon: 'success', title: 'Documento inviato', text: 'I firmatari riceveranno una email con il link per firmare. Per firmare serviranno un documento di identità e il codice ricevuto via SMS.' }
//     );
//     setLocation('/documenti_firma');
//   };

//   return (
//     <div className="flex flex-col min-h-screen">
//       <div className="flex-grow">
//         <PopoverComponentCustom title="Carica documento da firmare" />
//         <main className="mt-10 pb-8">
//           <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
//             <div className="flex items-center my-4">
//               <BackButton prevLocation={codiceFiscale ? `/dipendenti/${codiceFiscale}` : '/documenti_firma'} />
//             </div>

//             <div className="bg-white px-6 lg:px-8 mb-6">
//               <div className="mx-auto max-w-2xl text-center">
//                 <h2 className="text-2xl font-bold tracking-tight text-gray-900">Invia un documento da firmare</h2>
//               </div>
//             </div>

//             {fetching ? (
//               <Fetching />
//             ) : (
//               <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 lg:grid-cols-2">
//                 <div className="space-y-6">
//                   <div className="bg-white rounded-lg shadow p-6 space-y-4">
//                     <div>
//                       <label className={labelClass}>Dipendente</label>
//                       {codiceFiscale ? (
//                         employeeError ? (
//                           <p className="text-sm text-red-600">
//                             Impossibile caricare il dipendente. Torna alla pagina precedente e riprova.
//                           </p>
//                         ) : (
//                           employee && (
//                             <p className="text-sm text-gray-900">
//                               {employee.first_name} {employee.last_name} ({employee.cf})
//                             </p>
//                           )
//                         )
//                       ) : !employee ? (
//                         <ListBoxQueryMultiSelectEmployeesActiveContract
//                           token={token}
//                           selectedEmployees={[]}
//                           onChange={(sel) => {
//                             const picked = Array.isArray(sel) ? sel[0] : sel;
//                             if (picked && picked.id) setEmployee(picked);
//                           }}
//                           employeeRolePairs={[]}
//                           placeholder="Cerca dipendente..."
//                           filterBy="all"
//                           pageSize={10}
//                           openDirection="down"
//                         />
//                       ) : (
//                         <div className="flex items-center justify-between gap-2">
//                           <p className="text-sm text-gray-900">
//                             {employee.first_name} {employee.last_name} ({employee.cf})
//                           </p>
//                           <button
//                             type="button"
//                             onClick={() => setEmployee(null)}
//                             className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
//                           >
//                             Reset dipendente
//                           </button>
//                         </div>
//                       )}
//                     </div>

//                     <div>
//                       <label className={labelClass} htmlFor="firme-file">File PDF</label>
//                       <input
//                         id="firme-file"
//                         type="file"
//                         accept="application/pdf"
//                         onChange={handleFileChange}
//                         className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
//                       />
//                     </div>

//                     <div>
//                       <label className={labelClass} htmlFor="firme-name">Nome del documento</label>
//                       <input
//                         id="firme-name"
//                         type="text"
//                         value={name}
//                         onChange={(e) => setName(e.target.value)}
//                         className={inputClass}
//                       />
//                     </div>

//                     <div>
//                       <label className={labelClass} htmlFor="firme-description">Descrizione</label>
//                       <textarea
//                         id="firme-description"
//                         rows={3}
//                         value={description}
//                         onChange={(e) => setDescription(e.target.value)}
//                         className={inputClass}
//                       />
//                     </div>

//                     <div>
//                       <label className={labelClass} htmlFor="firme-due">Scadenza</label>
//                       <input
//                         id="firme-due"
//                         type="date"
//                         value={dueDate}
//                         onChange={(e) => setDueDate(e.target.value)}
//                         className={inputClass}
//                       />
//                     </div>
//                   </div>

//                   <div className="space-y-4">
//                     <h3 className="text-base font-semibold text-gray-900">Firmatari</h3>
//                     {signers.map((s, i) => (
//                       <FirmeSignerCard
//                         key={s.key}
//                         signer={s}
//                         title={s.isEmployee ? 'Dipendente' : `Firmatario ${i + 1}`}
//                         position={positions[s.key]}
//                         isActive={activeKey === s.key}
//                         onChange={(field, value) => updateSigner(s.key, field, value)}
//                         onRemove={s.isEmployee ? undefined : () => removeSigner(s.key)}
//                         onActivate={() => setActiveKey(s.key)}
//                       />
//                     ))}
//                     <div className="flex flex-wrap gap-2">
//                     <button
//                       type="button"
//                       onClick={addSigner}
//                       className="rounded-md border border-indigo-600 px-3 py-1.5 text-sm font-semibold text-indigo-600 hover:bg-indigo-50"
//                     >
//                       Aggiungi firmatario
//                     </button>
//                     {canAddLegal && (
//                       <button
//                         type="button"
//                         onClick={addLegalSigner}
//                         className="rounded-md border border-indigo-600 px-3 py-1.5 text-sm font-semibold text-indigo-600 hover:bg-indigo-50"
//                       >
//                         Aggiungi legale rappresentante
//                       </button>
//                     )}
//                     </div>
//                   </div>

//                   <div className="space-y-3">
//                     {missing.length > 0 && (
//                       <ul className="list-disc pl-5 text-sm text-red-600 space-y-1">
//                         {missing.map((m, i) => (
//                           <li key={i}>{m}</li>
//                         ))}
//                       </ul>
//                     )}
//                     <button
//                       type="submit"
//                       disabled={!canSubmit}
//                       className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
//                     >
//                       {submitting ? 'Invio in corso...' : 'Invia per la firma'}
//                     </button>
//                   </div>
//                 </div>

//                 <div>
//                   <FirmePdfSignaturePlacer
//                     file={file}
//                     signers={signers.map((s) => ({
//                       key: s.key,
//                       label: `${s.name} ${s.surname}`.trim() || 'Firmatario',
//                     }))}
//                     activeKey={activeKey}
//                     positions={positions}
//                     onPlace={(key, pos) => setPositions((p) => ({ ...p, [key]: pos }))}
//                     onActivate={setActiveKey}
//                   />
//                 </div>
//               </form>
//             )}
//           </div>
//         </main>
//       </div>
//       <Footer />
//     </div>
//   );
// }
